import React, { FC, FormEventHandler, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, ButtonProps, InputProps } from "@mui/material";
import { UserProfileType } from "@/types/view";
import { CommonUtils, LodashUtils } from "@/utils";
import { ButtonContainer, FormInput, FormWrapper, ProfileForm } from "./FormUserProfile.style";

type FormUserProfileProps = {
  profile: UserProfileType;
  buttonPrimaryLabel?: string;
  ButtonSecondaryProps?: ButtonProps;
  onSubmit: (profile: UserProfileType) => void;
  isProfile?: boolean;
};

const FormUserProfile: FC<FormUserProfileProps> = ({
  profile: defaultProfile,
  buttonPrimaryLabel,
  ButtonSecondaryProps,
  onSubmit,
  isProfile = false,
}) => {
  const { t: getLabel } = useTranslation();

  const [profile, setProfile] = useState<UserProfileType>(defaultProfile);
  const [isProfileNameError, setIsProfileNameError] = useState<boolean>(false);
  const [isProfilePhoneNumberError, setIsProfilePhoneNumberError] = useState<boolean>(false);
  const [isProfileEmailError, setIsProfileEmailError] = useState<boolean>(false);

  const handleChangeError = (name: string, value: string) => {
    switch (name) {
      case "name":
        setIsProfileNameError(Boolean(!value));
        break;
      case "email":
        setIsProfileEmailError(!CommonUtils.checkEmailFormat(value));
        break;
      case "phoneNumber":
        setIsProfilePhoneNumberError(!CommonUtils.checkPhoneNumberFormat(value));
        break;
      default:
        break;
    }
  };

  const handleChangeTextField: InputProps["onChange"] = event => {
    setProfile(currentState => ({
      ...currentState,
      [event.target.name]: event.target.value.trimStart(),
    }));
    handleChangeError(event.target.name, event.target.value.trimStart());
  };

  const handleSubmitForm: FormEventHandler = event => {
    event.preventDefault();
    onSubmit(profile);
  };

  useEffect(() => {
    if (defaultProfile) {
      setProfile({ ...defaultProfile });
    }
  }, [defaultProfile]);

  const isDisableSubmitButton =
    isProfileNameError ||
    isProfilePhoneNumberError ||
    isProfileEmailError ||
    LodashUtils.isEqual(defaultProfile, profile);

  return (
    <ProfileForm component="form" onSubmit={handleSubmitForm}>
      <FormWrapper>
        <FormInput
          fullWidth
          autoComplete="off"
          label={getLabel("L_FULLNAME")}
          error={isProfileNameError}
          name="name"
          value={profile.name}
          onChange={handleChangeTextField}
          onFocus={handleChangeTextField}
          required
        />
        <FormInput
          fullWidth
          autoComplete="off"
          label={getLabel("L_EMAIL")}
          error={isProfileEmailError}
          name="email"
          value={profile.email}
          onChange={handleChangeTextField}
          onFocus={handleChangeTextField}
          required
          disabled={isProfile}
        />
        <FormInput
          fullWidth
          autoComplete="off"
          label={getLabel("L_PHONE_NUMBER")}
          error={isProfilePhoneNumberError}
          name="phoneNumber"
          value={profile.phoneNumber}
          onChange={handleChangeTextField}
          onFocus={handleChangeTextField}
          required
        />
        <FormInput
          fullWidth
          autoComplete="off"
          label={getLabel("L_DEPARTMENT")}
          name="department"
          value={profile.department}
          onChange={handleChangeTextField}
          onFocus={handleChangeTextField}
        />
        <FormInput
          fullWidth
          autoComplete="off"
          label={getLabel("L_UNIT")}
          name="unit"
          value={profile.unit}
          onChange={handleChangeTextField}
          onFocus={handleChangeTextField}
        />
      </FormWrapper>
      <ButtonContainer>
        {ButtonSecondaryProps && (
          <Button variant="outlined" onClick={ButtonSecondaryProps.onClick}>
            {ButtonSecondaryProps.children}
          </Button>
        )}
        {buttonPrimaryLabel && (
          <Button type="submit" variant="contained" disabled={isDisableSubmitButton}>
            {buttonPrimaryLabel}
          </Button>
        )}
      </ButtonContainer>
    </ProfileForm>
  );
};

export default FormUserProfile;
